import { createElement, ReactElement } from "react";
import { toast, ToastOptions } from "react-hot-toast";

import { ApiError, isApiError } from "@config/apiClient";
import { ERROR_STRINGS } from "@config/messages";

export type ErrorResponse = {
  message?: string;
  detail?: string;
  __all__?: string[];
};

// A rejection body carries per-field messages nested as deep as the serializer — e.g.
// `{ attribute_values: [{ value: ["Revenue is required"] }] }`. Without this they all read as the
// generic fallback.
function collectFieldMessages(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(collectFieldMessages);
  if (value && typeof value === "object") return Object.values(value).flatMap(collectFieldMessages);
  return [];
}

// The server rejected the user's input and said why. Other statuses aren't trusted to carry
// text meant for a user.
const REJECTION_STATUS = 400;

function getRejectionMessages(error: ApiError | string): string[] | null {
  if (typeof error === "string") return null;
  if (!isApiError(error) || error.response?.status !== REJECTION_STATUS) return null;

  const data = error.response?.data as ErrorResponse | undefined;
  if (!data) return null;

  if (data.message) return [data.message];
  if (data.detail) return [data.detail];
  if (data.__all__?.length) return data.__all__;

  const fieldMessages = collectFieldMessages(data);
  return fieldMessages.length > 0 ? fieldMessages : null;
}

class ToastService {
  static showError(
    error: ApiError | string,
    userMessage?: ReactElement,
    options?: ToastOptions
  ): void {
    if (userMessage) {
      this.showUserError(createElement("span", null, userMessage), { id: "error", ...options });
      return;
    }

    const errorMessage = this.getErrorMessage(error);
    if (Array.isArray(errorMessage)) {
      errorMessage.forEach((message) => {
        toast.error(message, { id: message, ...options });
      });
    } else {
      toast.error(errorMessage, { id: errorMessage, ...options });
    }
  }

  /**
   * A rejected write. Shows the server's own reason ("Revenue is required") when it sent one, so a
   * validation failure isn't reduced to a generic line, and `fallback` otherwise.
   */
  static showRejection(
    error: ApiError | string,
    fallback: ReactElement,
    options?: ToastOptions
  ): void {
    const messages = getRejectionMessages(error);

    if (!messages) {
      this.showError(error, fallback, options);
      return;
    }

    messages.forEach((message) => toast.error(message, { id: message, ...options }));
  }

  static showUserError(message: ReactElement, options?: ToastOptions): void {
    toast.error(() => message, { id: "error", ...options });
  }

  static showSuccess(message: string, options?: ToastOptions): void {
    toast.success(message, { id: message, ...options });
  }

  static dismiss(id?: string): void {
    toast.dismiss(id);
  }

  static getErrorMessage(error: ApiError | string): string | string[] {
    if (typeof error === "string") {
      return error;
    } else if (error.response && error.response.data) {
      const responseData = error.response.data as ErrorResponse;
      if (responseData.message) return responseData.message;
      if (responseData.detail) return responseData.detail;
      if (responseData.__all__) return responseData.__all__;

      const fieldMessages = collectFieldMessages(responseData);
      return fieldMessages.length > 0 ? fieldMessages : ERROR_STRINGS.SERVER_ERROR;
    } else if (error.request) {
      return ERROR_STRINGS.SERVER_UNREACHABLE;
    } else {
      return error.message || ERROR_STRINGS.SERVER_ERROR;
    }
  }
}

export default ToastService;
